import { JSX, Show, createResource, createSignal } from "solid-js";
import { A, useNavigate, useParams } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import PageHeader from "../components/PageHeader";
import {
  api,
  failureSentence,
  type ChallengeRequestKind,
  type ChallengeView,
} from "../lib/api";
import { canMutate, useSession } from "../lib/session";
import { firmWords } from "../lib/orgWords";

/**
 * One reset key per instance, held until that reset succeeds, so an admin
 * retrying after a lost response replays the reset challenge-service already
 * committed instead of resetting the challenge twice.
 */
const resetKeys = new Map<string, string>();

function resetKey(instanceId: string): string {
  const existing = resetKeys.get(instanceId);
  if (existing) return existing;

  const key = crypto.randomUUID();
  resetKeys.set(instanceId, key);
  return key;
}

export default function ChallengeDetail() {
  const params = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const words = () => firmWords(session.admin());

  const [ch, { refetch }] = createResource(
    () => params.instanceId,
    (id) => api.getChallenge(id),
  );
  const [member] = createResource(
    () => ch()?.user_id,
    async (globalUserId) => (await api.getMember(globalUserId)).member,
  );

  const [resetting, setResetting] = createSignal(false);
  const [resetError, setResetError] = createSignal<string | null>(null);

  async function onReset() {
    if (
      !confirm(
        `Reset this ${words()?.run ?? ""}? The action will be recorded in the audit log.`,
      )
    ) {
      return;
    }
    setResetting(true);
    setResetError(null);
    try {
      const next = await api.resetChallenge(
        params.instanceId,
        resetKey(params.instanceId),
      );
      resetKeys.delete(params.instanceId);
      // The reset response IS the new instance — jump to it.
      navigate(`/challenges/${next.instance_id}`, { replace: true });
    } catch (e) {
      setResetError(failureSentence(e));
      setResetting(false);
      refetch();
    }
  }

  return (
    <>
      <AsyncResource resource={ch}>
        {(c) => (
          <>
            <PageHeader
              title={`${words()?.run_label ?? ""} ${c().instance_id.slice(0, 8)}…`}
              subtitle={`${member()?.email ?? c().user_id} · account ${c().account_number}`}
              actions={
                <>
                  <A class="btn-secondary" href="/challenges">
                    ← Back
                  </A>
                  <Show when={canMutate(session.admin()!.role)}>
                    <button
                      class="btn-danger"
                      disabled={resetting()}
                      onClick={onReset}
                    >
                      {resetting() ? "Resetting…" : `Reset ${words()?.run ?? ""}`}
                    </button>
                  </Show>
                </>
              }
            />

            <Show when={resetError()}>
              <div class="mb-4 text-sm text-red-600 border border-red-200 bg-red-50 p-3 rounded-sm">
                {resetError()}
              </div>
            </Show>

            <Show when={waitingRequest(c())}>
              {(sentence) => (
                <div class="mb-4 text-sm border border-amber-200 bg-amber-50 p-3 rounded-sm">
                  {sentence()}
                </div>
              )}
            </Show>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Field
                label="Status"
                value={<span class={statusClass(c().status)}>{c().status}</span>}
              />
              <Field label="Reset #" value={c().reset_number} />
              <Field label="Step" value={c().step_sequence} />
              <Field label="Initial balance" value={c().initial_balance} mono />
              <Field label="Total P&L" value={c().total_pnl} mono />
              <Field label="Daily P&L" value={c().daily_pnl} mono />
              <Field
                label="Current drawdown"
                value={c().current_drawdown ?? "—"}
                mono
              />
              <Field label="Peak balance" value={c().peak_balance ?? "—"} mono />
              <Field label="Peak equity" value={c().peak_equity ?? "—"} mono />
              <Field label="Trading days" value={c().trading_days} />
              <Field label="Profitable days" value={c().profitable_days} />
              <Field
                label="Started"
                value={new Date(c().started_at).toLocaleString()}
              />
            </div>

            <div class="card">
              <div class="card-header">
                <h2 class="text-sm font-semibold">Linked IDs</h2>
              </div>
              <div class="card-body grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Field label="Instance" value={c().instance_id} mono />
                <Field label="Account ID" value={c().account_id} mono />
                <Field label="User" value={member()?.email ?? "—"} />
                <Field label="Global user ID" value={c().user_id} mono />
                <Field
                  label="Template"
                  value={`${c().template_id} (rev ${c().revision_no})`}
                  mono
                />
                <Field label="Ruleset hash" value={c().ruleset_hash} mono />
                <Field label="Org ID" value={c().org_id} mono />
              </div>
            </div>
          </>
        )}
      </AsyncResource>
    </>
  );
}

const REQUESTED: Record<ChallengeRequestKind, string> = {
  pass_request: "The trader asked to move to the next step",
  approval: "Approval requested",
  activation: "Activation requested",
};

/**
 * The attempt's request that its place in the account's order has not
 * answered yet: an approval is answered 202 and decided there, within moments.
 */
function waitingRequest(c: ChallengeView): string | null {
  const request = c.newest_request;
  if (!request || request.answer) return null;
  return `${REQUESTED[request.kind]} at ${new Date(request.requested_at).toLocaleString()}: deciding at its place in the account's order.`;
}

function Field(props: {
  label: string;
  value: JSX.Element;
  mono?: boolean;
}) {
  return (
    <div class="text-sm">
      <div class="text-xs text-slate-500">{props.label}</div>
      <div classList={{ "mt-1": true, "font-mono text-xs": !!props.mono }}>
        {props.value}
      </div>
    </div>
  );
}

function statusClass(status: string): string {
  switch (status) {
    case "active":
      return "status-active";
    case "passed":
      return "status-passed";
    case "failed":
      return "status-failed";
    case "pending_review":
      return "status-pending";
    case "suspended":
      return "status-suspended";
    default:
      return "status-neutral";
  }
}
